import { useIntl } from '@umijs/max';
import React from 'react';
import {
  COLLAPSE_TRIGGER_SAFE_GAP,
  COLLAPSED_SIDER_THRESHOLD,
  clampSiderWidth,
  HEADER_HEIGHT,
  MAX_SIDER_WIDTH,
  MIN_SIDER_WIDTH,
  MOBILE_BREAKPOINT,
  persistSiderWidth,
  RESIZE_HANDLE_HITBOX,
  RESIZE_KEYBOARD_STEP,
  RESIZE_KEYBOARD_STEP_LARGE,
} from './layoutConstants';
import styles from './SiderResizeHandle.less';

export {
  DEFAULT_SIDER_WIDTH,
  getStoredSiderWidth,
  MAX_SIDER_WIDTH,
  MIN_SIDER_WIDTH,
  SIDER_WIDTH_STORAGE_KEY,
} from './layoutConstants';

type SiderResizeHandleProps = {
  enabled: boolean;
  siderWidth: number;
  onResizeWidth: (width: number) => void;
};

type HandleGeometry = {
  /** 侧栏右边界取整后的 x 坐标，细线画在边界内侧 1px */
  right: number;
  lineTop: number;
  hitTop: number;
};

const measureGeometry = (): HandleGeometry | null => {
  if (window.innerWidth <= MOBILE_BREAKPOINT) {
    return null;
  }

  const sider = document.querySelector<HTMLElement>('.ant-pro-sider');
  if (!sider) {
    return null;
  }

  const siderRect = sider.getBoundingClientRect();
  if (siderRect.width <= COLLAPSED_SIDER_THRESHOLD) {
    return null;
  }

  const header = document.querySelector<HTMLElement>('.ant-pro-layout-header');
  const lineTop = Math.round(
    header?.getBoundingClientRect().bottom ?? HEADER_HEIGHT,
  );
  const trigger = sider.querySelector<HTMLElement>(
    '.ant-pro-sider-collapsed-button',
  );
  const triggerBottom = trigger?.getBoundingClientRect().bottom;
  const hitTop =
    triggerBottom === undefined
      ? lineTop
      : Math.max(lineTop, Math.ceil(triggerBottom + COLLAPSE_TRIGGER_SAFE_GAP));

  return { right: Math.round(siderRect.right), lineTop, hitTop };
};

const SiderResizeHandle: React.FC<SiderResizeHandleProps> = ({
  enabled,
  siderWidth,
  onResizeWidth,
}) => {
  const intl = useIntl();
  const [geometry, setGeometry] = React.useState<HandleGeometry | null>(null);
  const [dragging, setDragging] = React.useState(false);
  const dragRef = React.useRef({
    pointerId: -1,
    startX: 0,
    startWidth: siderWidth,
    pendingWidth: null as number | null,
    frame: null as number | null,
  });
  const onResizeWidthRef = React.useRef(onResizeWidth);
  onResizeWidthRef.current = onResizeWidth;

  const commitWidth = React.useCallback((width: number) => {
    const next = clampSiderWidth(width);
    persistSiderWidth(next);
    onResizeWidthRef.current(next);
  }, []);

  React.useEffect(() => {
    if (!enabled) {
      setGeometry(null);
      return;
    }

    const update = () => {
      setGeometry((prev) => {
        const next = measureGeometry();
        if (
          prev &&
          next &&
          prev.right === next.right &&
          prev.lineTop === next.lineTop &&
          prev.hitTop === next.hitTop
        ) {
          return prev;
        }
        return next;
      });
    };

    update();
    window.addEventListener('resize', update);
    const sider = document.querySelector<HTMLElement>('.ant-pro-sider');
    const observer =
      sider && 'ResizeObserver' in window ? new ResizeObserver(update) : null;
    if (sider) {
      observer?.observe(sider);
    }

    return () => {
      window.removeEventListener('resize', update);
      observer?.disconnect();
    };
  }, [enabled, siderWidth]);

  React.useEffect(() => {
    if (!dragging) {
      return;
    }

    const drag = dragRef.current;
    const flush = () => {
      drag.frame = null;
      if (drag.pendingWidth !== null) {
        commitWidth(drag.pendingWidth);
        drag.pendingWidth = null;
      }
    };
    const handleMove = (event: PointerEvent) => {
      if (event.pointerId !== drag.pointerId) {
        return;
      }
      drag.pendingWidth = drag.startWidth + (event.clientX - drag.startX);
      if (drag.frame === null) {
        drag.frame = window.requestAnimationFrame(flush);
      }
    };
    const handleEnd = () => {
      if (drag.frame !== null) {
        window.cancelAnimationFrame(drag.frame);
      }
      flush();
      setDragging(false);
    };

    const { body } = document;
    const previousCursor = body.style.cursor;
    const previousUserSelect = body.style.userSelect;
    body.style.cursor = 'col-resize';
    body.style.userSelect = 'none';
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleEnd);
    window.addEventListener('pointercancel', handleEnd);

    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleEnd);
      window.removeEventListener('pointercancel', handleEnd);
      body.style.cursor = previousCursor;
      body.style.userSelect = previousUserSelect;
    };
  }, [dragging, commitWidth]);

  if (!enabled || !geometry) {
    return null;
  }

  const style = {
    '--resize-hit-left': `${geometry.right - RESIZE_HANDLE_HITBOX / 2}px`,
    '--resize-hit-top': `${geometry.hitTop}px`,
    '--resize-hit-width': `${RESIZE_HANDLE_HITBOX}px`,
    '--resize-line-offset': `${geometry.hitTop - geometry.lineTop}px`,
    '--resize-line-left': `${RESIZE_HANDLE_HITBOX / 2 - 1}px`,
  } as React.CSSProperties;

  return (
    <>
      {dragging && <div className={styles.dragMask} />}
      <div
        className={`${styles.hitBox}${dragging ? ` ${styles.dragging}` : ''}`}
        style={style}
        role="slider"
        tabIndex={0}
        aria-orientation="horizontal"
        aria-label={intl.formatMessage({
          id: 'pages.navigation.resizeSidebar',
        })}
        aria-valuemin={MIN_SIDER_WIDTH}
        aria-valuemax={MAX_SIDER_WIDTH}
        aria-valuenow={Math.round(siderWidth)}
        onPointerDown={(event) => {
          if (event.button !== 0) {
            return;
          }
          event.preventDefault();
          event.currentTarget.setPointerCapture?.(event.pointerId);
          dragRef.current.pointerId = event.pointerId;
          dragRef.current.startX = event.clientX;
          dragRef.current.startWidth = siderWidth;
          dragRef.current.pendingWidth = null;
          setDragging(true);
        }}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
            return;
          }
          event.preventDefault();
          const step = event.shiftKey
            ? RESIZE_KEYBOARD_STEP_LARGE
            : RESIZE_KEYBOARD_STEP;
          commitWidth(siderWidth + (event.key === 'ArrowRight' ? step : -step));
        }}
      >
        <span className={styles.line} aria-hidden="true" />
      </div>
    </>
  );
};

export default SiderResizeHandle;
